import { of, RetryConfig, throwError } from 'rxjs';
import { delay } from 'rxjs/operators';

export interface NetworkRetryConfig {
  /**
   * максимальное число попыток
   */
  maxAttempts: number;
  /**
   * задержка перед первой повторной попыткой
   */
  initialDelay: number;
  /**
   * максимальная задержка между попытками
   */
  maxDelay: number;
  /**
   * множитель величины интервала следующей попытки
   */
  backoffFactor: number;
}

export const DEFAULT_NETWORK_RETRY_CONFIG: NetworkRetryConfig = {
  maxAttempts: 5,
  initialDelay: 1000,
  maxDelay: 10000,
  backoffFactor: 2 // Дальнейшая попытка через 2 сек, затем 4 сек, ... до maxAttempts раз
};

/**
 * Без повторов (одна попытка). Для НЕидемпотентных/ротационных запросов, где повтор опасен: например,
 * обновление токена — при потерянном ответе refresh-токен уже ротирован, и повтор со старым токеном
 * получил бы 401 и разлогинил бы пользователя из-за микрообрыва сети.
 */
export const NO_RETRY_NETWORK_CONFIG: NetworkRetryConfig = {
  maxAttempts: 1,
  initialDelay: 0,
  maxDelay: 0,
  backoffFactor: 1
};

export const SHORTEN_NETWORK_RETRY_CONFIG: NetworkRetryConfig = {
  maxAttempts: 5,
  initialDelay: 500,
  maxDelay: 3000,
  backoffFactor: 1.5 // Дальнейшая попытка через 750 мс, затем 1125 мс, ... до maxAttempts раз
};

/**
 * Стратегия повторения попытки отправки, при которой в случае ошибки из-за нестабильного сетевого подключения
 *   происходит повторная попытка отправки в соответствии с заданными настройками.
 * @param config
 */
export const createNetworkRetryStrategy = (config: NetworkRetryConfig): RetryConfig => ({
  count: config.maxAttempts && config.maxAttempts > 0 ? config.maxAttempts - 1 : 0,
  delay: (error, retryCount: number) => {
    if (error.status === 0 || error.name === 'TimeoutError') {
      const delayTime = Math.min(
        config.initialDelay * Math.pow(config.backoffFactor, retryCount - 1),
        config.maxDelay
      );
      return of(null).pipe(delay(delayTime));
    }
    return throwError(() => error);
  }
});
